import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, Loader2, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err, "E-mail ou senha incorretos"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center px-4">
      <div data-testid="admin-login-modal" className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-lg p-8">
        <div className="flex flex-col items-center text-center">
          <img src="/brand/logo.jpg" alt="Igreja Unida Deus Proverá" className="h-24 w-auto rounded-2xl shadow-md object-cover" />
          <h1 className="font-heading mt-4 text-2xl font-extrabold text-slate-900">Área Administrativa</h1>
          <p className="mt-1 text-slate-500">Igreja Unida Deus Proverá</p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <Label htmlFor="adm-email" className="text-base font-semibold text-slate-700">E-mail</Label>
            <Input
              id="adm-email"
              data-testid="admin-login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              className="mt-1.5 h-12 text-base rounded-xl"
              autoComplete="email"
            />
          </div>
          <div>
            <Label htmlFor="adm-pwd" className="text-base font-semibold text-slate-700">Senha</Label>
            <div className="relative mt-1.5">
              <Input
                id="adm-pwd"
                data-testid="admin-login-password"
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 text-base rounded-xl pr-12"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Mostrar senha"
              >
                {showPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {error && (
            <p data-testid="admin-login-error" className="text-sm font-semibold text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3">
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full py-4 rounded-xl bg-blue-800 hover:bg-blue-700 disabled:opacity-60 text-white font-bold text-lg shadow-md transition-colors flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-5 h-5 animate-spin" />}
            Entrar
          </button>
        </form>

        <Link to="/" className="mt-6 flex items-center justify-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-blue-800 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Voltar ao painel público
        </Link>
      </div>
    </div>
  );
}
